// Generic route table + navigation guards shared by every app.
//
// Seam: the Logout route dispatches on the per-app store instance, which is
// created in the consuming app's src/store/index.js (see createBaseStore.js).
// This file lives in the shared library and cannot import that instance by
// relative path, so - like src/config.js - it is reached through the `@/`
// alias, which webpack resolves against the CONSUMING app's src/, not this
// library's own src/. Same pattern used by services/api.js and
// services/dialogService.js.
import store from "@/store";
import { takeHandoff } from "../../../shared-client/guestSession.mjs";
import { safeAuthNext, safeInternalPath } from "../utils/safePath";

import AdminView from "../views/AdminView.vue";
import SubscriptionView from "../views/SubscriptionView.vue";
import InviteFriendsView from "../views/InviteFriendsView.vue";
import BillingView from "../views/BillingView.vue";
import PricingView from "../views/PricingView.vue";
import SubscribeView from "../views/SubscribeView.vue";
import LoginView from "../views/LoginView.vue";
import RegisterView from "../views/RegisterView.vue";
import ProfileView from "../views/ProfileView.vue";
import ApiKeysView from "../views/ApiKeysView.vue";
import ForgotPasswordView from "../views/ForgotPasswordView.vue";
import ResetPasswordView from "../views/ResetPasswordView.vue";
import VerifyEmailView from "../views/VerifyEmailView.vue";
import FAQView from "../views/FAQView.vue";
import NotFoundView from "../views/NotFoundView.vue";

/**
 * Returns the array of generic (non-Home) route objects. The consuming app's
 * router.js prepends its own Home route and spreads this array in.
 * @param {object} config - the consuming app's config module (src/config.js),
 *   used here for PRODUCT_NAME in route titles.
 */
export function createBaseRoutes(config) {
  const PRODUCT_NAME = config.productName;

  return [
    {
      path: "/confirm-email-change",
      name: "ConfirmEmailChange",
      component: () => import("../views/ConfirmEmailChangeView.vue"),
      meta: { allowWhenUnpaid: true, hideAppChrome: true }
    },
    {
      path: "/admin",
      name: "Admin",
      component: AdminView,
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        // Admin screens never sit behind a plan: a gated admin still answers refunds and support.
        allowWhenUnpaid: true,
        title: `Admin | ${PRODUCT_NAME}`
      }
    },
    {
      path: "/admin/ops",
      name: "AdminOps",
      component: function () {
        return import("../views/AdminOpsView.vue");
      },
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        allowWhenUnpaid: true,
        title: `Operations Center | ${PRODUCT_NAME}`
      }
    },
    {
      path: "/admin/email",
      name: "AdminEmailCompose",
      component: function () {
        return import("../views/AdminEmailComposeView.vue");
      },
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        allowWhenUnpaid: true,
        title: `Send Email | ${PRODUCT_NAME}`
      }
    },
    {
      path: "/admin/issues",
      name: "AdminIssues",
      component: function () {
        return import("../views/AdminIssuesView.vue");
      },
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        allowWhenUnpaid: true,
        title: `Issues | ${PRODUCT_NAME}`
      }
    },
    {
      path: "/subscription",
      name: "Subscription",
      component: SubscriptionView,
      // allowWhenUnpaid: a gated free-tier user may reach this page to subscribe.
      meta: {
        requiresAuth: true,
        allowWhenUnpaid: true,
        title: `Subscription | ${PRODUCT_NAME}`
      }
    },
    {
      path: "/subscribe",
      name: "Subscribe",
      component: SubscribeView,
      // Dedicated first-time subscription page; destination of the force-paid gate.
      meta: {
        requiresAuth: true,
        allowWhenUnpaid: true,
        title: `Choose a plan | ${PRODUCT_NAME}`
      }
    },
    {
      path: "/invite-friends",
      name: "InviteFriends",
      component: InviteFriendsView,
      meta: { requiresAuth: true, title: `Invite Friends | ${PRODUCT_NAME}` }
    },
    {
      path: "/billing",
      name: "Billing",
      component: BillingView,
      meta: {
        requiresAuth: true,
        allowWhenUnpaid: true,
        title: `Billing | ${PRODUCT_NAME}`
      }
    },
    {
      path: "/pricing",
      name: "Pricing",
      component: PricingView,
      meta: { title: `Pricing | ${PRODUCT_NAME}` }
    },
    {
      path: "/faq",
      name: "FAQ",
      component: FAQView,
      meta: { requiresAuth: true, title: `FAQ | ${PRODUCT_NAME}` }
    },
    {
      path: "/login",
      name: "Login",
      component: LoginView,
      meta: { guest: true, title: `Login | ${PRODUCT_NAME}` }
    },
    {
      path: "/register",
      name: "Register",
      component: RegisterView,
      meta: { guest: true, title: `Register | ${PRODUCT_NAME}` }
    },
    {
      path: "/profile",
      name: "Profile",
      component: ProfileView,
      meta: { requiresAuth: true, title: `Profile | ${PRODUCT_NAME}` }
    },
    {
      path: "/api-keys",
      name: "ApiKeys",
      component: ApiKeysView,
      meta: { requiresAuth: true, title: `API Keys | ${PRODUCT_NAME}` }
    },
    {
      path: "/forgot-password",
      name: "ForgotPassword",
      component: ForgotPasswordView,
      meta: { guest: true, title: `Forgot Password | ${PRODUCT_NAME}` }
    },
    {
      path: "/reset-password/:token",
      name: "ResetPassword",
      component: ResetPasswordView,
      meta: { guest: true, title: `Reset Password | ${PRODUCT_NAME}` }
    },
    {
      path: "/verify-email/:token",
      name: "VerifyEmail",
      component: VerifyEmailView,
      meta: { guest: true, title: `Verify Email | ${PRODUCT_NAME}` }
    },
    {
      path: "/logout",
      name: "Logout",
      beforeEnter: (to, from, next) => {
        store.dispatch("logout");
        next("/login");
      }
    },
    // Last on purpose: every app spreads these routes after its own, so the
    // catch-all only answers URLs no route matched.
    {
      path: "*",
      name: "NotFound",
      component: NotFoundView,
      meta: { title: `Page not found | ${PRODUCT_NAME}` }
    }
  ];
}

/**
 * Sets document.title from the matched route's meta.title, falling back to
 * "<PRODUCT_NAME> Dashboard". Returns the afterEach callback to register.
 * @param {object} config - the consuming app's config module.
 */
export function createTitleGuard(config) {
  const PRODUCT_NAME = config.productName;
  return to => {
    const matchedWithTitle = [...to.matched]
      .reverse()
      .find(record => record.meta && record.meta.title);
    document.title = matchedWithTitle
      ? matchedWithTitle.meta.title
      : `${PRODUCT_NAME} Dashboard`;
  };
}

/**
 * Encapsulates the existing beforeEach navigation guard logic (auth,
 * force-paid gate, admin RBAC, guest routes). Returns the beforeEach
 * callback to register.
 * @param {import('vuex').Store} store - the app's Vuex store instance.
 * @param {object} config - the consuming app's config module (unused today,
 *   accepted for symmetry with createBaseRoutes/createTitleGuard and so a
 *   future per-app guard tweak has a place to read from).
 */
export function createRouterGuard(store, config) { // eslint-disable-line no-unused-vars
  return async (to, from, next) => {
    // Disabling the existing per-app opt-in also retires saved guest sessions.
    // Member sessions and apps that still allow guests keep their auth state.
    const storedUser = store.getters.currentUser;
    if (config && config.ALLOW_ANONYMOUS === false && storedUser && storedUser.is_anonymous) {
      store.commit("LOGOUT");
    }
    // Consume and remove sensitive fragments before auto-creating a guest.
    try {
      const incoming = takeHandoff(window.location, window.history);
      if (incoming) {
        if (config && config.ALLOW_ANONYMOUS === false) {
          next({ path: "/register", hash: "", replace: true });
          return;
        }
        const continuation = await store.dispatch("acceptGuestHandoff", incoming);
        next({ path: continuation.path, query: to.query, hash: "", replace: true });
        return;
      }
    } catch (error) {
      store.commit("SET_ERROR", "This session link expired or was already used. Return to the original page and try again.");
      next({ path: "/login", hash: "", replace: true });
      return;
    }
    const isAuthenticated = store.getters.isAuthenticated;
    const hasUser = !!store.getters.currentUser;
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
    const isGuestRoute = to.matched.some(record => record.meta.guest);

    // ALWAYS allow verify-email and reset-password routes
    // These must be accessible regardless of localStorage state (even if stale user data exists)
    if (to.name === "VerifyEmail" || to.name === "ResetPassword" || to.name === "ConfirmEmailChange") {
      next();
      return;
    }

    // For login/register pages, clear any stale auth data first
    // This handles the case where localStorage has user data but no valid token
    if (to.name === "Login" || to.name === "Register") {
      if (!isAuthenticated && hasUser) {
        // Stale user data without valid token - clear it
        store.commit("LOGOUT");
      }
      next();
      return;
    }

    // Force-paid gate: a signed-in user still on the free tier (and not
    // grandfathered via free_allowed_override) must subscribe before using the
    // app. requiresPaidUpgrade only returns true once public config + account
    // data have loaded, so this is a no-op during initial load and when the
    // FORCE_PAID_PLANS flag is off. Routes flagged allowWhenUnpaid (the subscribe
    // / subscription / billing pages) and the logout route stay reachable so the
    // user can pay or sign out.
    if (store.getters.requiresPaidUpgrade && to.name !== "Logout") {
      const allowWhenUnpaid = to.matched.some(
        record => record.meta && record.meta.allowWhenUnpaid
      );
      if (!allowWhenUnpaid) {
        // Checkout returns to the page they were headed for.
        const back = safeInternalPath(to.fullPath);
        next(back ? { path: "/subscribe", query: { next: back } } : "/subscribe");
        return;
      }
    }

    // Check if route requires authentication
    if (requiresAuth && !isAuthenticated) {
      // Remember where the visitor was headed.
      const remembered = () => {
        const destination = safeAuthNext(to.fullPath);
        return destination ? { next: destination } : {};
      };
      // Anonymous trial (per-app opt-in): routes flagged allowAnonymous get
      // a silently auto-created guest session instead of the login wall. A
      // paid-only site has no guests. A site without guest sessions (404)
      // sends the visitor to create an account; any other failure (API down)
      // to sign in.
      const allowAnonymous =
        config &&
        config.ALLOW_ANONYMOUS &&
        to.matched.some(record => record.meta.allowAnonymous);
      if (allowAnonymous && !store.getters.forcePaidPlans) {
        store
          .dispatch("anonymousLogin")
          .then(() => next())
          .catch(error => {
            const noGuests = !!(error && error.response && error.response.status === 404);
            next({ path: noGuests ? "/register" : "/login", query: remembered() });
          });
        return;
      }
      // Picking a plan, or a page a guest would have used, starts with
      // creating an account; every other page with signing in.
      next({
        path: to.name === "Subscribe" || allowAnonymous ? "/register" : "/login",
        query: remembered()
      });
    }
    // Check if route requires admin role (using proper RBAC)
    else if (to.matched.some(record => record.meta.requiresPlatformAdmin) && !store.getters.isPlatformAdmin) {
      next("/admin");
    }
    else if (requiresAdmin) {
      const user = store.getters.currentUser;
      const isAdmin = store.getters.isAdmin;
      if (!user || !isAdmin) {
        next("/"); // Redirect non-admins to home
      } else {
        next();
      }
    }
    // Check if guest route and user is already authenticated
    else if (isGuestRoute && (isAuthenticated || hasUser)) {
      next("/");
    } else {
      next();
    }
  };
}
